import {
    Image as ImageIcon,
    Video,
    Camera,
    Film,
    Music,
    Lock,
    Eraser,
    Sparkles,
    Maximize,
    Shirt,
    UserCircle,
    Baby,
    Move3d,
} from "lucide-react";
import { cn } from "@/lib/utils";

// Mapping Scenarios to underlying CreateModes
export type CreateScenario = 'image' | 'video' | 'photoshoot' | 'animate' | 'audio' | 'tools' | 'templates' | 'upscale' | 'tryon' | 'headshot' | 'babydance' | 'motion';

interface CreateHubScreenProps {
    onSelectScenario: (scenario: CreateScenario) => void;
}

export interface ScenarioTile {
    id: CreateScenario;
    title: string;
    description: string;
    icon: React.ElementType;
    badge?: { text: string; color: string };
    disabled?: boolean;
    className?: string; // For specific overrides
    hidden?: boolean;
}

export const SCENARIOS: ScenarioTile[] = [
    {
        id: 'image',
        title: 'Фото',
        description: 'Создание и редактирование изображений',
        icon: ImageIcon,
        className: "bg-gradient-to-br from-[#161B26] to-[#1C2230] hover:from-[#1C2230] hover:to-[#252b3b]"
    },
    {
        id: 'photoshoot',
        title: 'Фотосессия',
        description: 'Стили, позы и образы',
        icon: Camera,
        // badge: { text: 'PRO', color: 'bg-indigo-500/90 shadow-[0_0_10px_rgba(99,102,241,0.4)]' },
        className: "bg-gradient-to-br from-[#161B26] to-[#1C2230] hover:from-[#1C2230] hover:to-[#252b3b]"
    },
    {
        id: 'video',
        title: 'Видео',
        description: 'Видео из изображений или промптов',
        icon: Video,
        className: "bg-gradient-to-br from-[#161B26] to-[#1C2230] hover:from-[#1C2230] hover:to-[#252b3b]"
    },
    {
        id: 'templates',
        title: 'Шаблоны',
        description: 'Готовые промпты и пресеты',
        icon: Sparkles,
        className: "bg-gradient-to-br from-[#161B26] to-[#1C2230] hover:from-[#1C2230] hover:to-[#252b3b]",
    },
    {
        id: 'audio',
        title: 'Музыка',
        description: 'Песни и инструментал с AI',
        icon: Music,
        badge: { text: 'NEW', color: 'bg-primary/90 shadow-[0_0_10px_rgba(245,158,11,0.4)]' },
        className: "bg-gradient-to-br from-[#161B26] to-[#1C2230] hover:from-[#1C2230] hover:to-[#252b3b]"
    },
    {
        id: 'tools',
        title: 'Удаление фона',
        description: 'Удаление и замена фона',
        icon: Eraser,
        className: "bg-gradient-to-br from-[#161B26] to-[#1C2230] hover:from-[#1C2230] hover:to-[#252b3b]"
    },
    {
        id: 'upscale',
        title: 'Улучшение 4K',
        description: 'Улучшение качества до 4K',
        icon: Maximize,
        className: "bg-gradient-to-br from-[#161B26] to-[#1C2230] hover:from-[#1C2230] hover:to-[#252b3b]"
    },
    {
        id: 'tryon',
        title: 'Примерка',
        description: 'Примерка одежды на фото',
        icon: Shirt,
        className: "bg-gradient-to-br from-[#161B26] to-[#1C2230] hover:from-[#1C2230] hover:to-[#252b3b]"
    },
    {
        id: 'headshot',
        title: 'AI Портрет',
        description: 'Профессиональные портреты',
        icon: UserCircle,
        className: "bg-gradient-to-br from-[#161B26] to-[#1C2230] hover:from-[#1C2230] hover:to-[#252b3b]"
    },
    {
        id: 'babydance',
        title: 'Танец малыша',
        description: 'Анимация с забавными эффектами',
        icon: Baby,
        className: "bg-gradient-to-br from-[#161B26] to-[#1C2230] hover:from-[#1C2230] hover:to-[#252b3b]"
    },
    {
        id: 'motion',
        title: 'Kling Motion',
        description: 'Траектории движения и эффекты',
        icon: Move3d,
        className: "bg-gradient-to-br from-[#161B26] to-[#1C2230] hover:from-[#1C2230] hover:to-[#252b3b]"
    },
];

export function CreateHubScreen({ onSelectScenario }: CreateHubScreenProps) {
    return (
        <div className="flex flex-col min-h-full bg-transparent px-4 pt-4 pb-32 animate-in fade-in duration-500">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold tracking-tight text-foreground mb-1">
                    Создать
                </h1>
                <p className="text-muted-foreground/70 text-sm">
                    Что вы хотите создать сегодня?
                </p>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-2 gap-3">
                {SCENARIOS.filter(s => !s.hidden).map((scenario, i) => (
                    <button
                        key={scenario.id}
                        onClick={() => onSelectScenario(scenario.id)}
                        style={{ animationDelay: `${i * 40}ms` }}
                        className={cn(
                            "relative group flex flex-col text-left overflow-hidden",
                            "h-[140px] min-[380px]:h-[160px] w-full",
                            "rounded-[20px] min-[380px]:rounded-[24px]",
                            // Glass surface
                            "bg-white/[0.04] backdrop-blur-xl",
                            "border border-white/[0.08]",
                            // Shadows
                            "shadow-[0_8px_32px_-8px_rgba(0,0,0,0.5)]",
                            // Transitions
                            "transition-all duration-300 ease-out",
                            "active:scale-[0.97]",
                            // Animate in
                            "animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both",

                            scenario.disabled && "opacity-40 grayscale cursor-not-allowed"
                        )}
                    >
                         {/* Glow effect on active */}
                         <div className="absolute inset-0 rounded-[24px] opacity-0 group-active:opacity-100 transition-opacity duration-200 bg-gradient-to-br from-primary/10 via-transparent to-transparent pointer-events-none" />

                         <div className="relative h-full flex flex-col p-4 min-[380px]:p-5 z-10 w-full">
                            {/* Top Row: Icon + Badge */}
                            <div className="flex justify-between items-start mb-auto">
                                <div className={cn(
                                    "flex items-center justify-center w-11 h-11 rounded-2xl",
                                    "bg-white/[0.06] border border-white/[0.08]",
                                    "text-white/80 group-active:text-primary group-active:scale-110 transition-all duration-300"
                                )}>
                                    <scenario.icon className="w-5 h-5 stroke-[1.5]" />
                                </div>

                                {scenario.badge && (
                                    <span className={cn(
                                        "px-2.5 py-1 rounded-full",
                                        "bg-violet-500/20 text-violet-300 border border-violet-400/20",
                                        "text-[10px] font-bold tracking-wide"
                                    )}>
                                        {scenario.badge.text}
                                    </span>
                                )}

                                {scenario.disabled && !scenario.badge && (
                                    <div className="text-white/20">
                                        <Lock className="w-5 h-5" />
                                    </div>
                                )}
                            </div>

                            {/* Bottom Row: Text */}
                            <div className="space-y-0.5">
                                <h3 className="text-[16px] font-semibold text-white/90 tracking-tight leading-tight">
                                    {scenario.title}
                                </h3>
                                <p className="text-[12px] font-medium text-white/40 leading-snug line-clamp-2">
                                    {scenario.description}
                                </p>
                            </div>
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
}
